import debug from "debug";

const log = debug("app:fn");

/**
 * Manages the user interface for the context menu.
 * @category Components
 * @subcategory ContextMenu
 */
class ContextMenuUI {
  /**
   * Creates an instance of ContextMenuUI.
   * @param {Object} experience - The experience object containing the event hub.
   */
  constructor(experience) {
    /**
     * Event hub for emitting and listening to events.
     * @type {Object}
     */
    this.eventHub = experience.eventHub;

    /**
     * The DOM element for the context menu.
     * @type {HTMLElement|null}
     */
    this.menuElement = null;

    this.init();
  }

  /**
   * Initializes the context menu by creating, injecting, and setting up event listeners.
   */
  init() {
    this.createMenu();
    this.injectMenu();
    this.eventHub.on("contextMenu:show", (data) => this.show(data));
    this.eventHub.on("contextMenu:hide", () => this.hide());
    this.eventHub.on("contextMenu:paste", () => this.paste());
  }

  cleanup() {
    console.log("ContextMenuUI cleanup started...");
  
    // 1. Remove event listeners from eventHub
    if (this.handleContextMenuShow) {
      this.eventHub.off("contextMenu:show", this.handleContextMenuShow);
    }
    if (this.handleContextMenuHide) {
      this.eventHub.off("contextMenu:hide", this.handleContextMenuHide);
    }
    if (this.handleContextMenuPaste) {
      this.eventHub.off("contextMenu:paste", this.handleContextMenuPaste);
    }
  
    // 2. Remove context menu DOM element
    if (this.menuElement && this.menuElement.parentNode) {
      this.menuElement.parentNode.removeChild(this.menuElement);
    }
    this.menuElement = null;
  
    // 3. Clear object references
    this.eventHub = null;
  
    console.log("ContextMenuUI cleanup completed.");
  }

  /**
   * Handles context menu display logic based on input data.
   * @param {Object} data - Data for context menu display.
   */
  makeContextMenu(data) {
    log("data in make context", data);
    const { x, y, partNumber } = data;
    log("props", x, y, partNumber);
    this.show(x, y);
  }

  /**
   * Creates the context menu DOM structure.
   */
  createMenu() {
    if (document.getElementById("contextMenu")) {
      this.menuElement = document.getElementById("contextMenu");
      return;
    }

    this.menuElement = document.createElement("div");
    this.menuElement.id = "contextMenu";
    this.menuElement.className = "context-menu-bar";
    this.menuElement.style.display = "none";
    this.menuElement.innerHTML = `
      <ul>
        <li id="moreInfo">More Information</li>
        <li id="configurePartNumber">Configure</li>
        <li id="productPage">Product Page</li>
        <li id="sParam">S-Parameter Plot</li>
        <hr />
        <li id="copyOption">
          <span>Copy</span>
          <span>Ctrl+C</span>
        </li>
        <li id="pasteOption">
          <span>Paste</span>
          <span>Ctrl+V</span>
        </li>
        <li id="duplicateOption">Duplicate</li>
        <hr />
        <li id="chooseDiffPart">Choose Different Part</li>
        <hr />
        <li id="resetToDefault">Reset to Default</li>
        <li id="deleteOption">Delete</li>
      </ul>
    `;
  }

  /**
   * Injects the context menu into the document body.
   */
  injectMenu() {
    const bodyElement = document.querySelector("body");
    if (bodyElement) {
      bodyElement.appendChild(this.menuElement);
    } else {
      console.error("Body element not found. Unable to inject context menu.");
    }
  }

  /**
   * Displays the context menu at the specified position and updates the state of menu items.
   * @param {number} x - The x-coordinate for the context menu.
   * @param {number} y - The y-coordinate for the context menu.
   * @param {Array<Object>} models - Array of model objects.
   * @param {string|null} landingPage - URL for the product landing page.
   */
  show(data) {
    log("starting show data", data);
    const { x, y, models = [], landingPage = null } = data;
    log("starting show context menu", x, y, models, landingPage);

    const sParamURL = models[0]?.dataURL;

    const productPageElement = document.getElementById("productPage");
    const sParamElement = document.getElementById("sParam");

    // Reset both options first
    [productPageElement, sParamElement].forEach((el) => {
      if (el) {
        el.style.pointerEvents = "auto";
        el.style.color = "";
        el.style.cursor = "pointer";
      }
    });

    // Disable product page if landingPage is missing
    if (!landingPage && productPageElement) {
      productPageElement.style.pointerEvents = "none";
      productPageElement.style.color = "grey";
      productPageElement.style.cursor = "not-allowed";
    }

    // Disable sParam if no models or dataURL exists
    if (!sParamURL && sParamElement) {
      sParamElement.style.pointerEvents = "none";
      sParamElement.style.color = "grey";
      sParamElement.style.cursor = "not-allowed";
    }

    if (this.menuElement) {
      log("mouse props", x, y);
      this.menuElement.style.left = `${x}px`;
      this.menuElement.style.top = `${y}px`;
      this.menuElement.style.display = "block";
    }
  }

  /**
   * Hides the context menu.
   */
  hide() {
    if (this.menuElement) {
      this.menuElement.style.display = "none";
    }
  }

  /**
   * Updates the "Paste" option style to indicate it is enabled.
   */
  paste() {
    if (this.menuElement) {
      document.getElementById("pasteOption").style = "color: black;";
    }
  }
}

export default ContextMenuUI;
